import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot, Sparkles, CheckCircle2, AlertCircle, RefreshCw, Trash2,
  PhoneForwarded, BookOpen, Globe, MessageSquare, Phone, Search,
  Play, Square,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';

// Voice + language options exposed to the org admin. Voice ids map to the
// platform's built-in voices (current active set — the original set was
// retired 2026-03); labels stay brand-neutral (white-label rule).
const VOICES = [
  { value: 'Elliot', gender: 'Male', character: 'Friendly, professional' },
  { value: 'Savannah', gender: 'Female', character: 'Straightforward' },
  { value: 'Clara', gender: 'Female', character: 'Warm, professional' },
  { value: 'Emma', gender: 'Female', character: 'Warm, conversational' },
  { value: 'Layla', gender: 'Female', character: 'Bright, cheerful' },
  { value: 'Naina', gender: 'Female', character: 'Calm, professional' },
  { value: 'Kai', gender: 'Male', character: 'Relaxed, approachable' },
  { value: 'Nico', gender: 'Male', character: 'Young, casual' },
  { value: 'Sagar', gender: 'Male', character: 'Steady, professional' },
  { value: 'Neil', gender: 'Male', character: 'Clear, professional' },
  { value: 'Godfrey', gender: 'Male', character: 'Young, energetic' },
  { value: 'Sid', gender: 'Male', character: 'Smooth, deep-toned' },
  { value: 'Rohan', gender: 'Male', character: 'Bright, energetic' },
];
// Bundled 15s samples under public/voice-samples/ (self-hosted — the
// provider's sample URLs are signed/short-lived and leak its domain).
// Rohan ships no sample upstream.
const VOICES_WITH_SAMPLE = new Set(VOICES.map((v) => v.value).filter((v) => v !== 'Rohan'));
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'tr', label: 'Turkish' },
];
const FIRST_MESSAGE_MODES = [
  {
    value: 'assistant-speaks-first',
    label: 'Agent speaks first',
    hint: 'Opens with your fixed greeting below',
  },
  {
    value: 'assistant-speaks-first-with-model-generated-message',
    label: 'Agent improvises',
    hint: 'AI opens with a context-aware greeting',
  },
  {
    value: 'assistant-waits-for-user',
    label: 'Caller speaks first',
    hint: 'Agent stays silent until the caller talks',
  },
];

const TONES = [
  { value: 'professional', label: 'Professional', hint: 'Courteous and efficient' },
  { value: 'friendly', label: 'Friendly', hint: 'Warm and personable' },
  { value: 'casual', label: 'Casual', hint: 'Relaxed and conversational' },
  { value: 'energetic', label: 'Energetic', hint: 'Upbeat and enthusiastic' },
];

// OpenAI voices used when the org's provider is the GPT-Live gateway (no samples yet).
const OPENAI_VOICES = [
  { value: 'gleam', gender: 'Female', character: 'North American (natural)' },
  { value: 'meridian', gender: 'Male', character: 'North American (natural)' },
  { value: 'willow', gender: 'Female', character: 'Irish (natural)' },
  { value: 'stone', gender: 'Male', character: 'Irish (natural)' },
  { value: 'vesper', gender: 'Male', character: 'British (natural)' },
  { value: 'ripple', gender: 'Male', character: 'Australian (natural)' },
  { value: 'bossa', gender: 'Female', character: 'Brazilian Portuguese (natural)' },
  { value: 'tempo', gender: 'Male', character: 'Brazilian Portuguese (natural)' },
  { value: 'quartz', gender: 'Female', character: 'Australian (generated)' },
  { value: 'delta', gender: 'Female', character: 'Southern US (generated)' },
  { value: 'cinder', gender: 'Male', character: 'Southern US (generated)' },
  { value: 'beacon', gender: 'Male', character: 'Filipino English (generated)' },
  { value: 'marin', gender: 'Female (legacy realtime voice)', character: 'female (legacy Realtime voice)' },
  { value: 'cedar', gender: 'Male (legacy realtime voice)', character: 'male (legacy Realtime voice)' },
];

const EMPTY_DRAFT = {
  gptliveVoice: 'marin',
  basicConfig: { agentName: '', voice: 'Elliot', language: 'en' },
  personality: { tone: 'professional', styleNotes: '' },
  purpose: { jobDescription: '', greeting: '', firstMessageMode: 'assistant-speaks-first' },
  routing: { transferNumber: '', transferRules: '', defaultAssignee: '' },
  knowledge: { websiteUrl: '', businessProfile: '' },
  smsFollowUp: { enabled: false, template: '' },
  areaCodePreference: '',
};

function draftFromAgent(agent) {
  if (!agent) return EMPTY_DRAFT;
  return {
    gptliveVoice: agent.gptliveVoice || 'marin',
    basicConfig: {
      agentName: agent.basicConfig?.agentName || '',
      voice: agent.basicConfig?.voice || 'Elliot',
      language: agent.basicConfig?.language || 'en',
    },
    personality: {
      tone: agent.personality?.tone || 'professional',
      styleNotes: agent.personality?.styleNotes || '',
    },
    purpose: {
      jobDescription: agent.purpose?.jobDescription || '',
      greeting: agent.purpose?.greeting || '',
      firstMessageMode: agent.purpose?.firstMessageMode || 'assistant-speaks-first',
    },
    routing: {
      transferNumber: agent.routing?.transferNumber || '',
      transferRules: agent.routing?.transferRules || '',
      defaultAssignee: agent.routing?.defaultAssignee || '',
    },
    knowledge: {
      websiteUrl: agent.knowledge?.websiteUrl || '',
      // A fresh, unconfirmed distill takes the textarea so the admin
      // reviews it; Confirm Knowledge promotes it to businessProfile.
      businessProfile: agent.knowledge?.pendingProfile || agent.knowledge?.businessProfile || '',
    },
    smsFollowUp: {
      enabled: !!agent.smsFollowUp?.enabled,
      template: agent.smsFollowUp?.template || '',
    },
    areaCodePreference: agent.areaCodePreference || '',
  };
}

const Section = ({ title, Icon, subtitle, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <Icon size={20} className="text-teal-600" />
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Field = ({ label, children, hint, action }) => (
  <div>
    <div className="flex items-center justify-between gap-2 mb-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {action}
    </div>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-sm';

const STATUS_BADGES = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-700' },
  provisioning: { label: 'Activating…', cls: 'bg-amber-100 text-amber-800' },
  active: { label: '● Live', cls: 'bg-green-100 text-green-800' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700' },
};

const PhoneAgentSettings = () => {
  const [agent, setAgent] = useState(null);
  const [available, setAvailable] = useState(true);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [pendingFaqs, setPendingFaqs] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [confirmingKb, setConfirmingKb] = useState(false);
  const [suggesting, setSuggesting] = useState(null); // field name mid-generation
  const [previewingVoice, setPreviewingVoice] = useState(null); // voice id mid-playback
  const previewAudioRef = useRef(null);
  // Number picker (first activation only).
  const [areaCode, setAreaCode] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState('');
  // 'new' = search + buy; 'existing' = attach a number the platform already owns.
  const [numberMode, setNumberMode] = useState('new');
  const [ownedNumbers, setOwnedNumbers] = useState(null); // null = not loaded yet
  const [loadingOwned, setLoadingOwned] = useState(false);
  const [selectedOwnedSid, setSelectedOwnedSid] = useState('');
  const pollRef = useRef(null);

  const applyAgent = useCallback((a, { keepDraft = false } = {}) => {
    setAgent(a);
    if (!keepDraft) setDraft(draftFromAgent(a));
    setPendingFaqs((a?.knowledge?.pendingFaqs || []).map((f) => ({ question: f.question, answer: f.answer })));
  }, []);

  const load = useCallback(async (opts) => {
    try {
      const { data } = await api.get('/phone-agent');
      setAvailable(!!data.available);
      applyAgent(data.agent, opts);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load Phone Agent settings');
    } finally {
      setLoading(false);
    }
  }, [applyAgent]);

  useEffect(() => {
    load();
    api.get('/organizations/users', { silent: true })
      .then(({ data }) => setOrgUsers(Array.isArray(data?.users) ? data.users : []))
      .catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  // Poll while the website analysis job runs. The draft holds unsaved
  // form edits, so refreshes during the poll keep it intact — EXCEPT
  // the business profile: when the crawl finishes, its whole point is
  // to fill that field, so the fresh distill overwrites the textarea.
  const crawlStatus = agent?.knowledge?.crawlStatus || 'idle';
  const prevCrawlStatus = useRef(crawlStatus);
  useEffect(() => {
    if (prevCrawlStatus.current === 'running' && crawlStatus === 'done') {
      const fresh = agent?.knowledge?.pendingProfile || agent?.knowledge?.businessProfile || '';
      if (fresh) {
        setDraft((d) => ({ ...d, knowledge: { ...d.knowledge, businessProfile: fresh } }));
        toast.success('Website analyzed — review the business profile and suggested FAQs below');
      }
    }
    prevCrawlStatus.current = crawlStatus;
    if (crawlStatus !== 'running') {
      setAnalyzing(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    setAnalyzing(true);
    if (!pollRef.current) {
      pollRef.current = setInterval(() => load({ keepDraft: true }), 4000);
    }
  }, [crawlStatus, agent, load]);

  const set = (section, name, value) => {
    setDraft((d) => ({ ...d, [section]: { ...d[section], [name]: value } }));
  };

  // AI-draft one field from the current (possibly unsaved) wizard state.
  const suggest = async (field, section, name) => {
    setSuggesting(field);
    try {
      const { data } = await api.post('/phone-agent/suggest-copy', {
        field,
        context: {
          agentName: draft.basicConfig.agentName,
          tone: draft.personality.tone,
          styleNotes: draft.personality.styleNotes,
          businessProfile: draft.knowledge.businessProfile,
        },
      });
      set(section, name, data.text);
    } catch (err) {
      toast.error(err.response?.data?.error || 'AI suggestion failed');
    } finally {
      setSuggesting(null);
    }
  };

  const stopVoicePreview = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewingVoice(null);
  };

  // Per-card play: previewing is independent of selection so the admin
  // can audition voices back to back. Starting one stops the previous.
  const toggleVoicePreview = (voice) => {
    const wasPlaying = previewingVoice === voice;
    stopVoicePreview();
    if (wasPlaying || !VOICES_WITH_SAMPLE.has(voice)) return;
    const audio = new Audio(`${process.env.PUBLIC_URL || ''}/voice-samples/${voice.toLowerCase()}.mp3`);
    audio.onended = stopVoicePreview;
    audio.onerror = () => { stopVoicePreview(); toast.error('Preview not available for this voice'); };
    previewAudioRef.current = audio;
    setPreviewingVoice(voice);
    audio.play().catch(() => stopVoicePreview());
  };

  useEffect(() => stopVoicePreview, []); // stop playback on unmount

  const SuggestButton = ({ field, section, name }) => (
    <button type="button" onClick={() => suggest(field, section, name)} disabled={!!suggesting}
      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md disabled:opacity-50 flex-shrink-0">
      {suggesting === field ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
      Generate with AI
    </button>
  );

  const isGptLive = agent?.voiceProvider === 'gptlive';
  const save = async ({ silent = false } = {}) => {
    setSaving(true);
    try {
      const body = agent?.managedBy === 'platform'
        ? {
            knowledge: draft.knowledge,
            smsFollowUp: draft.smsFollowUp,
            routing: { defaultAssignee: draft.routing.defaultAssignee },
            areaCodePreference: draft.areaCodePreference,
          }
        : draft;
      const { data } = await api.put('/phone-agent', body);
      applyAgent(data.agent, { keepDraft: true });
      if (!silent) toast.success('Phone Agent settings saved');
      return data.agent;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const analyzeWebsite = async () => {
    const url = draft.knowledge.websiteUrl.trim();
    if (!url) { toast.error('Enter your website URL first'); return; }
    // Persist form edits first so the background job works on saved state.
    const saved = await save({ silent: true });
    if (!saved) return;
    try {
      const { data } = await api.post('/phone-agent/analyze-website', { websiteUrl: url });
      applyAgent(data.agent, { keepDraft: true });
      setAnalyzing(true);
      toast.success('Analyzing your website — this can take up to a minute');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start website analysis');
    }
  };

  const confirmKnowledge = async () => {
    setConfirmingKb(true);
    try {
      const { data } = await api.post('/phone-agent/confirm-knowledge', {
        businessProfile: draft.knowledge.businessProfile,
        faqs: pendingFaqs,
      });
      applyAgent(data.agent, { keepDraft: true });
      setDraft((d) => ({ ...d, knowledge: { ...d.knowledge, businessProfile: data.agent?.knowledge?.businessProfile || d.knowledge.businessProfile } }));
      const bits = [];
      if (data.faqsCreated) bits.push(`${data.faqsCreated} FAQ(s) added to your Knowledge Base`);
      if (data.republished) bits.push('live agent updated');
      toast.success(`Knowledge saved${bits.length ? ` — ${bits.join(', ')}` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save knowledge');
    } finally {
      setConfirmingKb(false);
    }
  };

  const searchNumbers = async () => {
    setSearchingNumbers(true);
    setAvailableNumbers([]);
    setSelectedNumber('');
    try {
      const { data } = await api.get('/phone-agent/available-numbers', { params: { areaCode } });
      setAvailableNumbers(data.numbers || []);
      if ((data.numbers || []).length === 0) toast('No numbers found for that area code — try another.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Number search failed');
    } finally {
      setSearchingNumbers(false);
    }
  };

  const loadOwnedNumbers = async () => {
    setLoadingOwned(true);
    setSelectedOwnedSid('');
    try {
      const { data } = await api.get('/phone-agent/owned-numbers');
      setOwnedNumbers(data.numbers || []);
    } catch (err) {
      setOwnedNumbers([]);
      toast.error(err.response?.data?.error || 'Could not load your existing numbers');
    } finally {
      setLoadingOwned(false);
    }
  };

  const switchNumberMode = (mode) => {
    setNumberMode(mode);
    if (mode === 'existing' && ownedNumbers === null && !loadingOwned) loadOwnedNumbers();
  };

  const hasNumber = !!(agent?.vapiPhoneNumberId || agent?.twilioNumberSid || agent?.phoneNumber);

  const activate = async () => {
    const pickingExisting = numberMode === 'existing';
    if (!hasNumber && (pickingExisting ? !selectedOwnedSid : !selectedNumber)) {
      toast.error(pickingExisting
        ? 'Pick one of your existing phone numbers first'
        : 'Search and pick a phone number for your agent first');
      return;
    }
    const saved = await save({ silent: true });
    if (!saved) return;
    setActivating(true);
    try {
      let body = {};
      if (!hasNumber) {
        body = pickingExisting ? { existingNumberSid: selectedOwnedSid } : { number: selectedNumber };
      }
      const { data } = await api.post('/phone-agent/provision', body);
      applyAgent(data.agent);
      toast.success(agent?.status === 'active' ? 'Phone Agent republished' : 'Phone Agent is live!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Activation failed');
      load({ keepDraft: true }); // pick up status=error + lastError
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <RefreshCw className="animate-spin text-teal-600" size={28} />
      </div>
    );
  }

  const status = agent?.status || 'draft';
  const badge = STATUS_BADGES[status] || STATUS_BADGES.draft;
  const isActive = status === 'active';
  // Assistant built by the Gain Rhino team on the voice platform and
  // linked from the superadmin panel: script/voice sections are hidden,
  // Save only refreshes the CRM-owned pieces.
  const isPlatform = agent?.managedBy === 'platform';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-xl mb-6">
          <div className="px-5 py-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Bot size={24} className="text-teal-600" />
                Phone Agent
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Your AI receptionist answers calls 24/7, captures leads, books appointments and answers questions from your website.
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
          </div>

          {!available && (
            <div className="mx-5 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800 flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              Phone Agent isn't enabled on this server yet. You can prepare your configuration now — contact support to activate calling.
            </div>
          )}

          {isPlatform && (
            <div className="mx-5 mb-4 p-3 bg-teal-50 border border-teal-200 rounded-md text-sm text-teal-900 flex items-start gap-2">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-teal-600" />
              <span>This agent is configured by the Gain Rhino team. Contact support to change its voice or script. Your knowledgebase, follow-up texts and call history work as usual.</span>
            </div>
          )}

          {status === 'error' && agent?.lastError && (
            <div className="mx-5 mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>Last activation failed: {agent.lastError}</span>
            </div>
          )}

          {isActive && agent?.phoneNumber && (
            <div className="mx-5 mb-4 p-4 bg-teal-50 border border-teal-200 rounded-md">
              <div className="flex flex-wrap items-center gap-3">
                <Phone size={18} className="text-teal-700" />
                <div>
                  <div className="text-sm text-teal-900 font-semibold">Your agent's number: <span className="font-mono">{agent.phoneNumber}</span></div>
                  <div className="text-xs text-teal-800 mt-1">
                    Forward your existing business line to this number (directly, or on busy / no answer — set this up with your phone carrier or PBX). Calls answered here appear on lead timelines and in your Inbox.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {!isPlatform && (<>
        {/* 1 — Basic Configuration */}
        <Section title="Basic Configuration" Icon={Bot} subtitle="Who your agent is and how it sounds.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Agent name" hint="Callers hear this name."
              action={<SuggestButton field="agentName" section="basicConfig" name="agentName" />}>
              <input type="text" className={inputCls} value={draft.basicConfig.agentName}
                placeholder="e.g. Maya"
                onChange={(e) => set('basicConfig', 'agentName', e.target.value)} />
            </Field>
            <Field label="Call language">
              <select className={inputCls} value={draft.basicConfig.language}
                onChange={(e) => set('basicConfig', 'language', e.target.value)}>
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Voice</label>
            {isGptLive ? (
              <>
                <p className="text-xs text-gray-500 mb-3">Your agent runs on the GPT-Live voice engine. Click a card to choose the OpenAI voice.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {OPENAI_VOICES.map((v) => {
                    const selected = draft.gptliveVoice === v.value;
                    return (
                      <div key={v.value} role="button" tabIndex={0}
                        onClick={() => setDraft((d) => ({ ...d, gptliveVoice: v.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDraft((d) => ({ ...d, gptliveVoice: v.value })); }}
                        className={`p-3 rounded-lg border text-left cursor-pointer transition-colors ${selected ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                        <div className="text-sm font-medium text-gray-900 capitalize">{v.value}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{v.gender}</div>
                        <div className="text-xs text-gray-500">{v.character}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
            <p className="text-xs text-gray-500 mb-3">Tap ▶ to hear a sample; click a card to choose.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {VOICES.map((v) => {
                const selected = draft.basicConfig.voice === v.value;
                const playing = previewingVoice === v.value;
                return (
                  <div key={v.value} role="button" tabIndex={0}
                    onClick={() => set('basicConfig', 'voice', v.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') set('basicConfig', 'voice', v.value); }}
                    className={`p-3 rounded-lg border text-left cursor-pointer transition-colors ${
                      selected
                        ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}>
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-sm font-medium text-gray-900">{v.value}</span>
                      {VOICES_WITH_SAMPLE.has(v.value) && (
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); toggleVoicePreview(v.value); }}
                          title={playing ? 'Stop sample' : 'Play sample'}
                          className={`p-1.5 rounded-full flex-shrink-0 ${
                            playing
                              ? 'bg-teal-600 text-white'
                              : 'text-teal-700 hover:bg-teal-100'
                          }`}>
                          {playing ? <Square size={12} /> : <Play size={12} />}
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{v.gender}</div>
                    <div className="text-xs text-gray-500">{v.character}</div>
                  </div>
                );
              })}
            </div>
              </>
            )}
          </div>
        </Section>

        {/* 2 — Personality & Tone */}
        <Section title="Personality & Tone" Icon={Sparkles} subtitle="How your agent talks to callers.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {TONES.map((t) => (
              <button key={t.value} type="button"
                onClick={() => set('personality', 'tone', t.value)}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  draft.personality.tone === t.value
                    ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}>
                <div className="text-sm font-medium text-gray-900">{t.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{t.hint}</div>
              </button>
            ))}
          </div>
          <Field label="Style notes (optional)" hint="Anything specific — phrases to use or avoid, how formal to be."
            action={<SuggestButton field="styleNotes" section="personality" name="styleNotes" />}>
            <textarea rows={2} className={inputCls} value={draft.personality.styleNotes}
              placeholder="e.g. Always thank the caller for choosing us. Avoid technical jargon."
              onChange={(e) => set('personality', 'styleNotes', e.target.value)} />
          </Field>
        </Section>

        {/* 3 — Purpose & Communication */}
        <Section title="Purpose & Communication" Icon={MessageSquare} subtitle="What the agent should accomplish on every call.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Who speaks first?</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {FIRST_MESSAGE_MODES.map((m) => (
                  <button key={m.value} type="button"
                    onClick={() => set('purpose', 'firstMessageMode', m.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      draft.purpose.firstMessageMode === m.value
                        ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}>
                    <div className="text-sm font-medium text-gray-900">{m.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{m.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            {draft.purpose.firstMessageMode === 'assistant-speaks-first' && (
              <Field label="Greeting" hint="The first thing callers hear. Leave blank for a default welcome."
                action={<SuggestButton field="greeting" section="purpose" name="greeting" />}>
                <input type="text" className={inputCls} value={draft.purpose.greeting}
                  placeholder="Thank you for calling Acme Dental! How can I help you today?"
                  onChange={(e) => set('purpose', 'greeting', e.target.value)} />
              </Field>
            )}
            <Field label="Job description" hint="Plain-language instructions. Default: answer questions, capture the caller's details, record their request."
              action={<SuggestButton field="jobDescription" section="purpose" name="jobDescription" />}>
              <textarea rows={3} className={inputCls} value={draft.purpose.jobDescription}
                placeholder="e.g. Answer questions about our services and hours. Always collect the caller's name and reason for calling. Offer to book a consultation."
                onChange={(e) => set('purpose', 'jobDescription', e.target.value)} />
            </Field>
          </div>
        </Section>
        </>)}

        {/* 4 — Call Routing & Transfer */}
        <Section title="Call Routing & Transfer" Icon={PhoneForwarded} subtitle="When to hand the call to a human, and who owns follow-ups.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!isPlatform && (
              <Field label="Transfer number (optional)" hint="Your team's real line. Leave blank to disable live transfers.">
                <input type="tel" className={inputCls} value={draft.routing.transferNumber}
                  placeholder="+1 555 123 4567"
                  onChange={(e) => set('routing', 'transferNumber', e.target.value)} />
              </Field>
            )}
            <Field label="Default assignee" hint="Appointment and callback tasks go to this teammate.">
              <select className={inputCls} value={draft.routing.defaultAssignee || ''}
                onChange={(e) => set('routing', 'defaultAssignee', e.target.value)}>
                <option value="">— Select a teammate —</option>
                {orgUsers.map((u) => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
              </select>
            </Field>
          </div>
          {!isPlatform && (
          <div className="mt-4">
            <Field label="Transfer rules (optional)" hint="Situations where the agent should transfer immediately."
              action={<SuggestButton field="transferRules" section="routing" name="transferRules" />}>
              <textarea rows={2} className={inputCls} value={draft.routing.transferRules}
                placeholder="e.g. The caller is in pain or has an emergency; the caller is an existing patient asking about billing."
                onChange={(e) => set('routing', 'transferRules', e.target.value)} />
            </Field>
          </div>
          )}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="mt-1 h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                checked={draft.smsFollowUp.enabled}
                onChange={(e) => set('smsFollowUp', 'enabled', e.target.checked)} />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">Text the caller after the call</div>
                <div className="text-xs text-gray-500">Requires an SMS-enabled line in VoIP Settings.</div>
                {draft.smsFollowUp.enabled && (
                  <div className="flex justify-end mt-2">
                    <SuggestButton field="smsTemplate" section="smsFollowUp" name="template" />
                  </div>
                )}
                {draft.smsFollowUp.enabled && (
                  <textarea rows={2} className={`${inputCls} mt-1`} value={draft.smsFollowUp.template}
                    placeholder="Thanks for calling Acme Dental! Book online anytime at acmedental.com or reply to this message."
                    onChange={(e) => set('smsFollowUp', 'template', e.target.value)} />
                )}
              </div>
            </label>
          </div>
          {!hasNumber && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-900 mb-1">Pick your agent's phone number</div>
              <div className="flex flex-wrap gap-2 mb-3" role="tablist">
                {[
                  { id: 'new', label: 'Buy a new number' },
                  { id: 'existing', label: 'Use a number you already own' },
                ].map((t) => (
                  <button key={t.id} type="button" role="tab" aria-selected={numberMode === t.id}
                    onClick={() => switchNumberMode(t.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                      numberMode === t.id
                        ? 'bg-teal-600 border-teal-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {numberMode === 'new' && (
                <>
                  <p className="text-xs text-gray-500 mb-3">Search available local numbers by area code, pick one, then activate. The number is purchased when you activate.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="text" className={`${inputCls} w-28`} value={areaCode} maxLength={3}
                      placeholder="213"
                      onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ''))} />
                    <button type="button" onClick={searchNumbers} disabled={searchingNumbers}
                      className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 disabled:opacity-50 text-sm font-medium flex items-center gap-2">
                      {searchingNumbers ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                      Search Numbers
                    </button>
                  </div>
                  {availableNumbers.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {availableNumbers.map((n) => (
                        <label key={n.number}
                          className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer text-sm ${
                            selectedNumber === n.number
                              ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}>
                          <input type="radio" name="agentNumber" className="h-4 w-4 text-teal-600 focus:ring-teal-500"
                            checked={selectedNumber === n.number}
                            onChange={() => setSelectedNumber(n.number)} />
                          <span className="font-mono">{n.friendlyName || n.number}</span>
                          {(n.locality || n.region) && (
                            <span className="text-xs text-gray-500 truncate">{[n.locality, n.region].filter(Boolean).join(', ')}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}

              {numberMode === 'existing' && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <p className="text-xs text-gray-500">Numbers already on your account that aren't attached to a Phone Agent yet. Nothing is purchased — the number is attached when you activate.</p>
                    <button type="button" onClick={loadOwnedNumbers} disabled={loadingOwned}
                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:border-gray-400 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5">
                      <RefreshCw size={12} className={loadingOwned ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                  </div>
                  {loadingOwned && ownedNumbers === null && (
                    <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading your numbers…</div>
                  )}
                  {ownedNumbers && ownedNumbers.length === 0 && !loadingOwned && (
                    <div className="text-sm text-gray-500">No unused numbers found on your account. Buy a new one instead, or contact support to add your existing line.</div>
                  )}
                  {ownedNumbers && ownedNumbers.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {ownedNumbers.map((n) => (
                        <label key={n.sid}
                          className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer text-sm ${
                            selectedOwnedSid === n.sid
                              ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}>
                          <input type="radio" name="agentOwnedNumber" className="h-4 w-4 text-teal-600 focus:ring-teal-500"
                            checked={selectedOwnedSid === n.sid}
                            onChange={() => setSelectedOwnedSid(n.sid)} />
                          <span className="font-mono">{n.number}</span>
                          {n.friendlyName && n.friendlyName !== n.number && (
                            <span className="text-xs text-gray-500 truncate">{n.friendlyName}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Section>

        {/* 5 — Knowledgebase */}
        <Section title="Knowledgebase" Icon={BookOpen}
          subtitle="Teach your agent about the business — auto-build it from your website, then fine-tune.">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Field label="Website URL">
                <input type="url" className={inputCls} value={draft.knowledge.websiteUrl}
                  placeholder="https://www.yourbusiness.com"
                  onChange={(e) => set('knowledge', 'websiteUrl', e.target.value)} />
              </Field>
            </div>
            <button type="button" onClick={analyzeWebsite} disabled={analyzing}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2">
              {analyzing ? <RefreshCw size={16} className="animate-spin" /> : <Globe size={16} />}
              {analyzing ? 'Analyzing…' : (agent?.knowledge?.lastCrawledAt ? 'Re-analyze Website' : 'Analyze Website')}
            </button>
          </div>

          {crawlStatus === 'failed' && agent?.knowledge?.crawlError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>Website analysis failed: {agent.knowledge.crawlError}. You can still fill in the business profile below manually.</span>
            </div>
          )}

          <div className="mt-4">
            <Field label="Business profile"
              hint="What the agent knows by heart — services, hours, locations, contact info. Edit freely; this is included on every call."
              action={<SuggestButton field="businessProfile" section="knowledge" name="businessProfile" />}>
              <textarea rows={8} className={`${inputCls} font-mono text-xs`} value={draft.knowledge.businessProfile}
                placeholder="Analyze your website to auto-fill this, or write it yourself."
                onChange={(e) => set('knowledge', 'businessProfile', e.target.value)} />
            </Field>
          </div>

          {pendingFaqs.length > 0 && (
            <div className="mt-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  Suggested FAQs from your website ({pendingFaqs.length})
                </h3>
                <span className="text-xs text-gray-500">Review, edit or remove — then confirm to add them to your Knowledge Base.</span>
              </div>
              <ul className="space-y-3">
                {pendingFaqs.map((f, i) => (
                  <li key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0 space-y-2">
                        <input type="text" className={inputCls} value={f.question}
                          onChange={(e) => setPendingFaqs((arr) => arr.map((x, j) => j === i ? { ...x, question: e.target.value } : x))} />
                        <textarea rows={2} className={inputCls} value={f.answer}
                          onChange={(e) => setPendingFaqs((arr) => arr.map((x, j) => j === i ? { ...x, answer: e.target.value } : x))} />
                      </div>
                      <button type="button" onClick={() => setPendingFaqs((arr) => arr.filter((_, j) => j !== i))}
                        className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50 flex-shrink-0">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {(pendingFaqs.length > 0 || draft.knowledge.businessProfile !== (agent?.knowledge?.businessProfile || '')) && (
              <button type="button" onClick={confirmKnowledge} disabled={confirmingKb}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2">
                {confirmingKb ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Confirm Knowledge
              </button>
            )}
            <Link to="/knowledge-base" className="text-sm text-indigo-600 hover:text-indigo-800">
              Manage full Knowledge Base →
            </Link>
          </div>
        </Section>

        {/* Actions */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => save()} disabled={saving}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 text-sm font-medium">
            {saving ? 'Saving…' : 'Save Draft'}
          </button>
          <button type="button" onClick={activate} disabled={activating || !available}
            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2">
            {activating && <RefreshCw size={16} className="animate-spin" />}
            {isActive ? (isPlatform ? 'Save & Sync' : 'Save & Republish') : status === 'error' ? 'Retry Activation' : 'Activate Phone Agent'}
          </button>
          {isActive && (
            <span className="text-xs text-gray-500">
              Republishing updates your live agent instantly — its number never changes.
            </span>
          )}
        </div>

      </div>
    </div>
  );
};

export default PhoneAgentSettings;
